import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { TABLE_URL_PARAMS } from 'store/reportsManager/constants'
import { useManagedUrlSearchParams } from 'hooks/useManagedUrlSearchParams'

/**
 * Validates and normalizes table URL search parameters using validators.
 *
 * RESPONSIBILITIES:
 * - Validates URL params using validator.validate functions
 * - Normalizes/clamps values using validator.normalize functions
 * - Handles preferredDefaults (e.g., from Redux) when URL is empty
 * - Maps validator keys to URL param names
 * - Delegates URL management to useManagedUrlSearchParams
 * - Returns validated params with validator keys (not URL param names)
 *
 * RELATIONSHIP:
 * - Higher-level hook built on useManagedUrlSearchParams
 * - Uses validators from createTableValidators
 * - Used by container components for table param management
 *
 * HOOK HIERARCHY:
 *
 * useManagedUrlSearchParams (low-level)
 *   ↓ used by
 * useValidatedTableParams (high-level)
 *   ↓ used by
 * Container components
 *
 * Each layer adds functionality:
 * - useManagedUrlSearchParams: URL management
 * - useValidatedTableParams: + validation/normalization
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.validators - Validators object from createTableValidators.
 *   Each validator has: { default, validate, normalize, urlParamName }
 * @param {Array<string>} config.managedParamKeys - Validator keys or URL param names that should be in URL.
 *   Example: ['sortKey', 'orderBy', 'currentPage'] or [TABLE_URL_PARAMS.SITE_ID, TABLE_URL_PARAMS.SORT_KEY]
 *   The hook accepts either validator keys (e.g., 'siteId') or URL param names (e.g., 'site') and converts them internally.
 *   Returns validated values with validator keys regardless of input format.
 * @param {Object} [config.urlParamKeyOverrides] - Optional mapping from validator keys to URL param names.
 *   Example: { siteId: 'customSite', sortKey: 'sort' }
 *   If not provided, uses validators[].urlParamName or TABLE_URL_PARAMS defaults.
 * @param {Function} [config.onValidatedParamsChange] - Callback when validated params change.
 *   Signature: (validatedParams: Object) => void
 *   Receives validated and normalized params with validator keys.
 *   Only fires when params actually change.
 * @param {boolean} [config.isSettled] - Whether `managedParamKeys` has reached its final state and won't grow.
 *   When `true`, removes params in `scopedParams` but not in `managedParamKeys` (even if never managed).
 *   When `false` or `undefined`, preserves params that might become managed (e.g., while data loads).
 *   Use this when data is hydrated and `managedParamKeys` is stable (e.g., `Array.isArray(data)`).
 * @returns {Object} - Returns validated values and update function:
 *   - validated: Object with validated/normalized values using validator keys
 *     Example: { sortKey: 'schools', orderBy: 'asc', currentPage: 1, siteId: 123 }
 *   - updateUrlParams: Function to update URL params using validator keys
 *     Signature: (updates: Object, method?: 'replace' | 'push') => void
 *     Example: updateUrlParams({ sortKey: 'status' })
 *
 * @example
 * // Using createTableValidators:
 * const validators = createTableValidators({
 *   validSortKeys: ['schools', 'status'],
 *   defaultSortKey: 'schools',
 * })
 *
 * const { validated, updateUrlParams } = useValidatedTableParams({
 *   validators,
 *   managedParamKeys: ['sortKey', 'orderBy'],
 * })
 *
 * // Use validated values
 * const sortKey = validated.sortKey      // Always present
 * const orderBy = validated.orderBy      // Always present
 */
export const useValidatedTableParams = ({
  validators = {},
  managedParamKeys = [],
  urlParamKeyOverrides,
  onValidatedParamsChange,
  isSettled = false,
}) => {
  const location = useLocation()

  // ============================================
  // SETUP: Create mappings between validator keys and URL param names
  // ============================================

  // Create default mapping from validator keys to TABLE_URL_PARAMS
  const defaultUrlParamNames = useMemo(
    () => ({
      siteId: TABLE_URL_PARAMS.SITE_ID,
      sortKey: TABLE_URL_PARAMS.SORT_KEY,
      orderBy: TABLE_URL_PARAMS.ORDER_BY,
      currentPage: TABLE_URL_PARAMS.CURRENT_PAGE,
      classInstance: TABLE_URL_PARAMS.CLASS_INSTANCE,
    }),
    [],
  )

  // Create mapping from validator keys to URL param names
  // Priority: urlParamKeyOverrides > validator.urlParamName > defaultUrlParamNames > key
  const urlParamMapping = useMemo(() => {
    const mapping = {}
    Object.keys(validators).forEach(key => {
      mapping[key] =
        urlParamKeyOverrides?.[key] ||
        validators[key]?.urlParamName ||
        defaultUrlParamNames[key] ||
        key
    })
    return mapping
  }, [validators, urlParamKeyOverrides, defaultUrlParamNames])

  // Create reverse mapping from URL param names to validator keys
  // Include both the mapped URL param name and the default URL param name
  // This allows managedParamKeys to use either TABLE_URL_PARAMS constants or custom names
  const urlParamToValidatorKey = useMemo(() => {
    const mapping = {}
    Object.keys(urlParamMapping).forEach(validatorKey => {
      const urlParamName = urlParamMapping[validatorKey]
      mapping[urlParamName] = validatorKey
      // Also map the default URL param name if it's different
      // This allows TABLE_URL_PARAMS.SITE_ID ('site') to map to 'siteId' even when overridden
      const defaultUrlParamName = defaultUrlParamNames[validatorKey]
      if (defaultUrlParamName && defaultUrlParamName !== urlParamName) {
        mapping[defaultUrlParamName] = validatorKey
      }
    })
    return mapping
  }, [urlParamMapping, defaultUrlParamNames])

  // Helper to get URL param name for validator key
  const getUrlParamName = useCallback(
    validatorKey => {
      return urlParamMapping[validatorKey] || validatorKey
    },
    [urlParamMapping],
  )

  // Helper to get validator key for URL param name
  const getValidatorKey = useCallback(
    urlParamName => {
      return urlParamToValidatorKey[urlParamName] || urlParamName
    },
    [urlParamToValidatorKey],
  )

  // ============================================
  // DERIVED VALUES: Extract defaults and scoped params from validators
  // ============================================

  // Convert managedParamKeys from URL param names to validator keys
  // This allows containers to use TABLE_URL_PARAMS constants (e.g., 'site')
  // while the hook works with validator keys internally (e.g., 'siteId')
  const managedParamKeysAsValidatorKeys = useMemo(() => {
    return managedParamKeys.map(key => {
      // First check if it's already a validator key
      if (key in validators) {
        return key
      }
      // Try to convert URL param name to validator key
      const validatorKey = getValidatorKey(key)
      // If conversion found a mapping, use it; otherwise assume it's already a validator key
      return validatorKey !== key ? validatorKey : key
    })
  }, [managedParamKeys, getValidatorKey, validators])

  // Extract defaults from validators (for managed params only)
  // Convert validator keys to URL param names for useManagedUrlSearchParams
  // NOTE: Params without defaults (e.g., classInstance) are included with null
  // This allows useManagedUrlSearchParams to manage them (read from URL, validate, but don't add if missing)
  // Also includes params in managedParamKeys even if validator doesn't exist yet (e.g., classInstance before data loads)
  const defaults = useMemo(() => {
    const result = {}
    managedParamKeysAsValidatorKeys.forEach(validatorKey => {
      const urlParamName = getUrlParamName(validatorKey)
      if (validatorKey in validators) {
        // Validator exists - include with default (or null if no default)
        // Include param even if no default (use null as placeholder)
        // This allows the hook to read and validate the param from URL
        // Params without defaults won't be added to URL if missing, but will be managed if present
        result[urlParamName] =
          validators[validatorKey]?.default !== undefined
            ? validators[validatorKey].default
            : null
      } else {
        // Validator doesn't exist yet (e.g., classInstance before validClassInstances is available)
        // Still include it with null so useManagedUrlSearchParams can manage it
        // This preserves the param in URL until validator is created and can validate it
        result[urlParamName] = null
      }
    })
    return result
  }, [validators, managedParamKeysAsValidatorKeys, getUrlParamName])

  // Extract scoped params (all URL param names from validators)
  // This defines the scope of params this hook manages for cleanup
  const scopedParams = useMemo(() => {
    return Object.keys(validators).map(validatorKey =>
      getUrlParamName(validatorKey),
    )
  }, [validators, getUrlParamName])

  // ============================================
  // FUNCTIONS: Create validateParam function for useManagedUrlSearchParams
  // ============================================

  // Create validateParam function that uses validators
  // This normalizes values using validator.normalize or validator.validate + default
  const validateParam = useCallback(
    (urlParamName, value) => {
      // Convert URL param name to validator key
      const validatorKey = getValidatorKey(urlParamName)
      const validator = validators[validatorKey]

      if (!validator) {
        // No validator - return value as-is
        return value
      }

      // Use normalize if available (preferred - handles clamping, type conversion)
      if (validator.normalize) {
        return validator.normalize(value, validator.default)
      }

      // Fallback: validate and use default if invalid
      const isValid = validator.validate ? validator.validate(value) : true

      return isValid ? value : validator.default
    },
    [validators, getValidatorKey],
  )

  // ============================================
  // DELEGATE: Use base hook for URL management
  // ============================================

  const { managedParams, updateUrlParams } = useManagedUrlSearchParams({
    defaults,
    scopedParams,
    validateParam,
    shouldSetDefaults: true,
    // Note: onParamsChange not used - we use onValidatedParamsChange instead
    // If managedParamKeys is explicitly empty array, this is intentional (not initialization)
    // Pass false to indicate we want cleanup, not preservation
    isInitializing: managedParamKeys.length === 0 ? false : undefined,
    isSettled,
  })

  // ============================================
  // DERIVED VALUES: Convert managedParams back to validator keys
  // ============================================

  // Convert managedParams (URL param names) back to validated object (validator keys)
  // Also handle preferredDefaults (e.g., from Redux) when URL is empty
  const validated = useMemo(() => {
    const result = {}
    const currentSearchParams = new URLSearchParams(location.search)

    // Iterate over all validators to get validated values
    Object.keys(validators).forEach(validatorKey => {
      const validator = validators[validatorKey]
      if (!validator) return

      const defaultValue = validator.default
      const preferredDefault = validator.preferredDefault // From Redux (optional)
      const urlParamName = getUrlParamName(validatorKey)
      const urlHasParam = currentSearchParams.has(urlParamName)
      const isManaged = managedParamKeysAsValidatorKeys.includes(validatorKey)

      // Priority: URL > preferredDefault > default
      let finalValue

      if (urlHasParam) {
        // URL has value
        if (isManaged) {
          // Managed param - already validated/normalized by useManagedUrlSearchParams
          // Just translate the key (URL param name → validator key), value is already normalized
          finalValue = managedParams[urlParamName]
        } else {
          // Not managed - validate/normalize it ourselves
          const urlValue = currentSearchParams.get(urlParamName)
          finalValue = validateParam(urlParamName, urlValue)
        }
      } else if (preferredDefault !== undefined) {
        // URL is empty - check preferredDefault (Redux values)
        // Validate preferredDefault before using it
        const isValidPreferred = validator.validate
          ? validator.validate(preferredDefault)
          : true
        const valueToNormalize = isValidPreferred
          ? preferredDefault
          : defaultValue

        // Normalize the value (preferredDefault might not be normalized)
        if (validator.normalize) {
          finalValue = validator.normalize(valueToNormalize, defaultValue)
        } else {
          finalValue = valueToNormalize
        }
      } else {
        // No URL param and no preferredDefault - use default
        // Normalize default to ensure it's valid (e.g., invalid siteId → first valid siteId)
        // normalize handles validation internally and returns a valid value (or safe fallback)
        if (validator.normalize) {
          finalValue = validator.normalize(defaultValue, defaultValue)
        } else {
          // No normalize function - use default as-is
          // (Ideally validators should always have normalize to handle invalid values)
          finalValue = defaultValue
        }
      }

      result[validatorKey] = finalValue
    })

    return result
  }, [
    validators,
    location.search,
    managedParams,
    managedParamKeysAsValidatorKeys,
    getUrlParamName,
    validateParam,
  ])

  // Filter validated to only include managedParamKeys (using validator keys)
  const validatedFinal = useMemo(() => {
    const filtered = {}
    managedParamKeysAsValidatorKeys.forEach(validatorKey => {
      if (validatorKey in validated) {
        filtered[validatorKey] = validated[validatorKey]
      }
    })

    return filtered
  }, [validated, managedParamKeysAsValidatorKeys])

  // ============================================
  // FUNCTIONS: Wrap updateUrlParams to translate validator keys to URL param names
  // ============================================

  const updateUrlParamsTranslated = useCallback(
    (updates, method = 'replace') => {
      // Translate validator keys to URL param names
      const translatedUpdates = {}
      Object.keys(updates).forEach(validatorKey => {
        const urlParamName = getUrlParamName(validatorKey)
        translatedUpdates[urlParamName] = updates[validatorKey]
      })
      return updateUrlParams(translatedUpdates, method)
    },
    [updateUrlParams, getUrlParamName],
  )

  // ============================================
  // SIDE EFFECTS: Callback synchronization
  // ============================================

  // Call onValidatedParamsChange when validated params change
  const prevValidatedParamsRef = useRef({})
  useEffect(() => {
    if (!onValidatedParamsChange) return

    // Check if validated params actually changed
    const prevParams = prevValidatedParamsRef.current
    const currentKeys = Object.keys(validatedFinal)
    const prevKeys = Object.keys(prevParams)

    const hasChanged =
      currentKeys.length !== prevKeys.length ||
      currentKeys.some(key => validatedFinal[key] !== prevParams[key]) ||
      prevKeys.some(key => !(key in validatedFinal))

    if (hasChanged && Object.keys(validatedFinal).length > 0) {
      prevValidatedParamsRef.current = { ...validatedFinal }
      onValidatedParamsChange(validatedFinal)
    }
  }, [validatedFinal, onValidatedParamsChange])

  return {
    validated: validatedFinal,
    updateUrlParams: updateUrlParamsTranslated,
  }
}

// Re-export validator creation functions for convenience
export { createTableValidators, injectReduxValues } from './createValidators'
